import { Icon } from "@/components/Icon";
import "./KpiCard.css";

interface KpiCardProps {
  label: string;
  value: string;
  detail?: string;
  tone?: "up" | "down" | "neutral";
  /**
   * `hero` renders the card as a dark panel with a gold figure.
   *
   * EXACTLY ONE card per row should use it. Four cards of equal weight give the
   * eye nowhere to land, so it starts top-left by default and reads the row as a
   * list — which is how the first version of this dashboard read across a booth
   * table. One dark panel makes the row a hierarchy instead: the number that
   * matters, and three that support it.
   */
  variant?: "default" | "hero";
  /**
   * True when `value` is derived from the live entries feed rather than a
   * fixed seeded figure (see Dashboard.tsx's DEMO_KPIS comment for which is
   * which and why). Draws a small dot next to the label — the same "Live"
   * signal the header pill uses — so a visitor can see WHICH numbers are
   * wired up before they ever watch one move. Omitted entirely (not `false`)
   * on every fixed card, so this is additive: a card that never passes it
   * renders byte-for-byte what it always has.
   */
  live?: boolean;

  /**
   * True when this card IS wired to the feed but the feed is not currently connected — so
   * the figure shown is the last one received rather than a current one. Only meaningful
   * alongside `live`; a fixed card was never claiming to update.
   */
  stale?: boolean;
  /**
   * A signed delta ("-$413") to flash in beside the value for as long as the
   * arrival that produced it is still glowing in the activity feed —
   * Dashboard.tsx clears this the moment `useEntries`' own highlight window
   * closes, so the two land and fade together instead of running on a second,
   * invented timer. `null`/omitted renders nothing extra: a fixed card never
   * needs this prop at all.
   */
  flashDelta?: string | null;
}

export function KpiCard({
  label,
  value,
  detail,
  tone = "neutral",
  variant = "default",
  live = false,
  stale = false,
  flashDelta = null,
}: KpiCardProps) {
  return (
    <div
      className={
        `kpi-card${variant === "hero" ? " kpi-card--hero" : ""}` +
        (flashDelta ? " kpi-card--pulse" : "")
      }
      // `role="group"` + `aria-label` gives every card an unambiguous accessible
      // name (rules/ui-breadcrumbs.md #2: role + name over test ids) — a screen
      // reader announces which figure a card is about, and a test can scope to
      // exactly one card by label instead of guessing from DOM order or a class.
      role="group"
      aria-label={label}
      // Machine-readable breadcrumb (rules/ui-breadcrumbs.md #1): a tester reads
      // this instead of inferring "does this number move" from two screenshots.
      data-kpi-status={live ? (stale ? "stale" : "live") : "fixed"}
    >
      <span className="kpi-card__label">
        {label}
        {live && (
          <>
            <span className="kpi-card__live-dot" aria-hidden="true" />
            {/*
              THE DOT AND ITS SENTENCE MUST NOT OUTLIVE THE FEED.

              MEASURED 2026-09-07 by stopping postgres under a running wall. Recent activity
              said, correctly and in red, **"Can't reach EasyFarm right now"** — and eighteen
              inches to its left three cards printed exact figures beside this dot, whose
              screen-reader sentence read "live figure, updates from the activity feed". It
              was not updating and could not. The numbers were simply the last ones fetched.
              The screen contradicted itself on the point a prospect would ask about.

              Not blanked, because the last known figure is still the best answer available
              and a card that empties itself mid-conversation is worse. It says WHEN it is
              from instead, and the dot stops claiming otherwise.
            */}
            <span className="visually-hidden">
              {stale
                ? " — last figure received; the activity feed is not connected right now"
                : " — live figure, updates from the activity feed"}
            </span>
          </>
        )}
      </span>
      {/* aria-live: this is the one number on the card that actually changes
          post-mount. Wrapping just this row (not the whole card) means a
          screen-reader user hears the new value and its delta together,
          once, without the label/detail being re-announced on every update. */}
      <span className="kpi-card__value-row" aria-live="polite">
        {/*
          data-testid, deliberately and narrowly — rules/ui-breadcrumbs.md §2 allows it
          "only to disambiguate repeated role+name pairs", and this is that case. The
          value and the flash chip are both money strings inside one group with one
          accessible name, so NO text pattern can separate them: a locator loose enough
          to match a negative value ("-$15,596", which net farm income legitimately is
          now that it comes from the ledger) also matches the chip ("-$3,603"), and one
          tight enough to exclude the chip cannot read a negative value. That is a
          strict-mode violation either way, and it cost a real debugging session.

          These two ids are therefore a TESTED CONTRACT, not scattered hooks: e2e/
          dashboard.spec.ts reads them by name. Renaming one breaks that suite loudly,
          which is the point.
        */}
        <span className="kpi-card__amount" data-testid="kpi-value">{value}</span>
        {flashDelta && (
          <span className="kpi-card__flash-amount" data-testid="kpi-flash">{flashDelta}</span>
        )}
      </span>
      {detail && (
        <span className={`kpi-card__detail kpi-card__detail--${tone}`}>
          {/* Drawn arrow rather than the ▲/▼ characters this shipped with: those
              are font glyphs that render at a different weight and baseline than
              the text beside them, and on Windows they pick up the emoji font and
              turn BLUE. An SVG inherits currentColor and cannot drift. */}
          {tone !== "neutral" && (
            <Icon name={tone === "up" ? "arrow-up" : "arrow-down"} size={13} strokeWidth={2.2} />
          )}
          {detail}
        </span>
      )}
    </div>
  );
}
